import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  Box,
  Button,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_GROWTH_AGGREGATION,
  DEFAULT_GROWTH_PERIOD_DAYS,
  GROWTH_ANALYTICS_AGGREGATIONS,
  GROWTH_ANALYTICS_PERIODS,
  hasGrowthAnalyticsData,
  normalizeGrowthAnalytics,
} from "./growthAnalyticsConstants";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const SERIES_USERS = "کاربران جدید";
const SERIES_REQUESTS = "درخواست‌های احداث نیروگاه";
const SERIES_CONVERSION = "نرخ تبدیل";
const BAR_COLORS = ["#1976d2", "#2e7d32"];
const LINE_COLOR = "#ed6c02";

const formatPersianNumber = (value) =>
  Number(value || 0).toLocaleString("fa-IR");

const toPersianDigits = (value) =>
  String(value ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

const compactDayLabel = (dateStr, aggregation = 1) => {
  if (!dateStr) return "";
  // Monthly/weekly labels already come as readable Persian text from API.
  if (aggregation === 2 || aggregation === 3) {
    return toPersianDigits(dateStr);
  }
  const parts = String(dateStr).split("/");
  if (parts.length >= 3) return toPersianDigits(`${parts[1]}/${parts[2]}`);
  return toPersianDigits(dateStr);
};

const KpiCard = ({ label, value, color = "#1976d2" }) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: "12px",
      bgcolor: "#f0f6ff",
      border: "1px solid #e3edf9",
      height: "100%",
    }}
  >
    <Typography fontSize={12} color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography fontWeight={800} fontSize={20} color={color} sx={{ mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

const GrowthConversionAnalyticsPanel = ({
  analytics,
  loading = false,
  height = 280,
  showHeader = true,
  showPeriodFilter = true,
  showAggregationFilter,
  showFullAnalyticsLink = false,
  periodDays = DEFAULT_GROWTH_PERIOD_DAYS,
  aggregation = DEFAULT_GROWTH_AGGREGATION,
  onPeriodChange,
  onAggregationChange,
}) => {
  const navigate = useNavigate();
  const canShowAggregationFilter =
    showAggregationFilter ?? typeof onAggregationChange === "function";
  const normalized = useMemo(
    () => normalizeGrowthAnalytics(analytics),
    [analytics]
  );
  const trend = normalized.trend;
  const summary = normalized.summary;
  const hasData = hasGrowthAnalyticsData(normalized);
  const activeAggregation = Number(
    aggregation || normalized.aggregation || DEFAULT_GROWTH_AGGREGATION
  );

  const chartMeta = useMemo(
    () =>
      trend.map((item) => ({
        x: compactDayLabel(item.date, activeAggregation),
        fullDate: toPersianDigits(item.date || ""),
        users: item.newUsersCount || 0,
        requests: item.powerPlantRequestsCount || 0,
        conversion: Number(item.conversionRate || 0),
      })),
    [trend, activeAggregation]
  );

  const comboData = useMemo(
    () => ({
      labels: chartMeta.map((item) => item.x),
      datasets: [
        {
          type: "bar",
          label: SERIES_USERS,
          data: chartMeta.map((item) => item.users),
          backgroundColor: BAR_COLORS[0],
          borderColor: BAR_COLORS[0],
          borderWidth: 1,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: SERIES_REQUESTS,
          data: chartMeta.map((item) => item.requests),
          backgroundColor: BAR_COLORS[1],
          borderColor: BAR_COLORS[1],
          borderWidth: 1,
          yAxisID: "y",
          order: 3,
        },
        {
          type: "line",
          label: SERIES_CONVERSION,
          data: chartMeta.map((item) => item.conversion),
          borderColor: LINE_COLOR,
          backgroundColor: LINE_COLOR,
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.25,
          yAxisID: "y1",
          order: 1,
        },
      ],
    }),
    [chartMeta]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: true,
          position: "top",
          rtl: true,
          labels: { font: { family: "dana FD" }, usePointStyle: true },
        },
        tooltip: {
          rtl: true,
          callbacks: {
            title: (items) => {
              const idx = items?.[0]?.dataIndex;
              return idx != null && chartMeta[idx] ? chartMeta[idx].fullDate : "";
            },
            label: () => null,
            afterBody: (items) => {
              const idx = items?.[0]?.dataIndex;
              if (idx == null || !chartMeta[idx]) return [];
              const row = chartMeta[idx];
              return [
                `${SERIES_USERS}: ${formatPersianNumber(row.users)}`,
                `درخواست‌ها: ${formatPersianNumber(row.requests)}`,
                `${SERIES_CONVERSION}: ${formatPersianNumber(row.conversion)}٪`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: "rgba(0,0,0,0.06)" },
          ticks: {
            maxRotation: activeAggregation === 3 ? 40 : 45,
            minRotation: activeAggregation === 3 ? 0 : 45,
            autoSkip: activeAggregation === 1,
            font: { size: activeAggregation === 3 ? 11 : 10, family: "dana FD" },
          },
        },
        y: {
          type: "linear",
          position: "right",
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.06)" },
          ticks: {
            callback: (val) => formatPersianNumber(val),
            font: { family: "dana FD" },
          },
          title: {
            display: true,
            text: "تعداد",
            font: { family: "dana FD" },
          },
        },
        y1: {
          type: "linear",
          position: "left",
          beginAtZero: true,
          suggestedMax: 100,
          grid: { drawOnChartArea: false },
          ticks: {
            callback: (val) => `${formatPersianNumber(val)}٪`,
            font: { family: "dana FD" },
          },
          title: {
            display: true,
            text: "نرخ تبدیل",
            font: { family: "dana FD" },
          },
        },
      },
    }),
    [chartMeta, activeAggregation]
  );

  const handlePeriodChange = (_, next) => {
    if (next == null) return;
    const option = GROWTH_ANALYTICS_PERIODS.find((p) => p.days === next);
    if (!option?.enabled) return;
    onPeriodChange?.(next);
  };

  const handleAggregationChange = (_, next) => {
    if (next == null) return;
    onAggregationChange?.(next);
  };

  const periodLabel =
    GROWTH_ANALYTICS_PERIODS.find((p) => p.days === periodDays)?.label ||
    `${periodDays} روز`;
  const aggregationLabel =
    GROWTH_ANALYTICS_AGGREGATIONS.find((p) => p.value === activeAggregation)
      ?.label || "روزانه";

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary" fontSize={14}>
          در حال بارگذاری...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ direction: "rtl" }}>
      {showHeader ? (
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography fontWeight={700} fontSize={15}>
              {activeAggregation === 3
                ? "مقایسه ماهانه جذب و تبدیل"
                : "روند جذب و تبدیل کاربران"}
            </Typography>
            <Typography fontSize={13} color="text.secondary">
              {periodLabel} گذشته · {aggregationLabel}
            </Typography>
          </Box>
          {showFullAnalyticsLink ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/SLN/MarketGrowth/GrowthAnalytics")}
              sx={{ borderRadius: "10px" }}
            >
              مشاهده تحلیل کامل
            </Button>
          ) : null}
        </Box>
      ) : null}

      {showPeriodFilter ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={periodDays}
          onChange={handlePeriodChange}
          sx={{ mb: 1, flexWrap: "wrap", mr: 1 }}
        >
          {GROWTH_ANALYTICS_PERIODS.map((option) => (
            <ToggleButton
              key={option.days}
              value={option.days}
              disabled={!option.enabled}
              sx={{
                fontSize: 12,
                px: 1.5,
                opacity: option.enabled ? 1 : 0.45,
              }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}

      {canShowAggregationFilter ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeAggregation}
          onChange={handleAggregationChange}
          sx={{ mb: 1.5, flexWrap: "wrap" }}
          color="primary"
        >
          {GROWTH_ANALYTICS_AGGREGATIONS.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{ fontSize: 12, px: 1.5, fontWeight: 700 }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="کاربران جدید"
            value={formatPersianNumber(summary.totalNewUsers)}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="درخواست‌های ثبت شده"
            value={formatPersianNumber(summary.totalRequests)}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="میانگین نرخ تبدیل"
            value={`${formatPersianNumber(summary.averageConversionRate)}٪`}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      {!hasData ? (
        <Box
          sx={{
            height,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fafbfc",
            borderRadius: "12px",
            border: "1px dashed #e0e0e0",
            px: 2,
          }}
        >
          <ShowChartIcon sx={{ fontSize: 40, color: "#bdbdbd", mb: 1 }} />
          <Typography color="text.secondary" fontSize={14} textAlign="center">
            نمایش داده‌ای برای این بازه زمانی وجود ندارد.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height, position: "relative" }}>
          <Chart type="bar" options={options} data={comboData} />
        </Box>
      )}
    </Box>
  );
};

export default GrowthConversionAnalyticsPanel;
